import { programOptions } from './filterOptions'

export interface ProgramContent {
  programId: number
  areaId: number
  name: string
  headline: string
  description: string
  whyStudy: string
  careerOutlook: string
  salaryRange: string
  jobTitles?: string[]
  whatToLookFor?: string
  sections?: {
    heading: string
    body: string
  }[]
  faq?: {
    question: string
    answer: string
  }[]
}

// Program-specific content for major programs
export const programContent: Record<number, ProgramContent> = {
  // Business Programs
  101: {
    programId: 101,
    areaId: 1,
    name: 'Accounting',
    headline: 'Top Accredited Online Accounting Programs of 2026',
    description:
      'Launch your career in accounting with an online degree. Prepare for CPA certification and roles in public accounting, corporate finance, and forensic accounting.',
    whyStudy:
      'Accounting is the language of business. An accounting degree provides clear career paths, strong earning potential, and the foundation for CPA certification. Accountants are essential to every organization, offering job security and opportunities for advancement.',
    careerOutlook:
      'Employment of accountants and auditors is projected to grow 4 percent through 2032, adding about 126,500 new jobs. The median annual wage for accountants is $79,880, with the top 10% earning over $132,690.',
    salaryRange: '$60,000 - $132,000+',
    jobTitles: [
      'Staff Accountant',
      'Tax Accountant',
      'Auditor',
      'Financial Analyst',
      'Controller',
      'Forensic Accountant',
      'Management Accountant',
      'Cost Accountant',
    ],
    whatToLookFor:
      'Look for AACSB-accredited programs that align with CPA exam requirements in your state. The best online accounting programs offer courses in financial accounting, managerial accounting, taxation, auditing, and accounting information systems. Ensure the program includes at least 150 credit hours if you plan to pursue CPA licensure.',
    sections: [
      {
        heading: 'Why Choose an Online Accounting Degree?',
        body: "Online accounting programs offer the same rigorous curriculum as on-campus programs, with the flexibility to study while working. Most programs are designed to meet the 150-credit-hour requirement for CPA licensure, and many offer specialized tracks in tax, audit, or forensic accounting. You'll learn Generally Accepted Accounting Principles (GAAP), financial reporting, tax law, and accounting software like QuickBooks and SAP.",
      },
      {
        heading: 'CPA Certification Path',
        body: "Most states require 150 credit hours of education to sit for the CPA exam—30 hours beyond a typical bachelor's degree. Many online accounting programs are structured as 5-year programs or offer a combined bachelor's/master's pathway to meet this requirement. The CPA credential significantly increases earning potential and career opportunities, with CPAs earning 10-15% more than non-certified accountants.",
      },
      {
        heading: 'Career Paths in Accounting',
        body: 'Accounting graduates work in public accounting firms (the Big Four and regional firms), corporate accounting departments, government agencies, and non-profit organizations. You can specialize in tax accounting, audit, forensic accounting, management accounting, or financial analysis. Many accountants advance to controller, CFO, or partner positions.',
      },
      {
        heading: "What You'll Learn",
        body: 'Core coursework includes Financial Accounting, Managerial Accounting, Intermediate Accounting I & II, Cost Accounting, Auditing, Taxation, Accounting Information Systems, and Business Law. Advanced programs cover topics like fraud examination, international accounting standards (IFRS), and data analytics for accountants.',
      },
    ],
    faq: [
      {
        question: 'Can I become a CPA with an online accounting degree?',
        answer:
          "Yes. As long as your program is accredited and meets your state's educational requirements (typically 150 credit hours), an online accounting degree qualifies you to sit for the CPA exam. Most states also require 1-2 years of work experience under a licensed CPA.",
      },
      {
        question:
          'How long does it take to complete an online accounting degree?',
        answer:
          "A bachelor's degree typically takes 4 years full-time, though many online programs offer accelerated options. If you're pursuing the 150 credit hours required for CPA licensure, plan for 5 years or consider a combined bachelor's/master's program.",
      },
      {
        question: 'What is the starting salary for accounting graduates?',
        answer:
          'Entry-level accountants earn between $45,000 and $55,000 annually, depending on location and employer. With CPA certification and 3-5 years of experience, salaries typically rise to $70,000-$90,000. Senior accountants and controllers earn $100,000+.',
      },
      {
        question: 'Do I need to be good at math to study accounting?',
        answer:
          "You don't need advanced calculus skills. Accounting primarily involves arithmetic, algebra, and logical thinking. The focus is on understanding financial concepts, applying accounting principles, and analyzing business transactions rather than complex mathematical computations.",
      },
    ],
  },
  102: {
    programId: 102,
    areaId: 1,
    name: 'Business Administration',
    headline: 'Top Accredited Online Business Administration Programs of 2026',
    description:
      'Build versatile business skills with a business administration degree. Prepare for management roles across industries with comprehensive training in operations, strategy, and leadership.',
    whyStudy:
      'A business administration degree is one of the most versatile credentials, opening doors to management positions in virtually any sector.',
    careerOutlook:
      'Strong demand for business managers across industries, with median salaries ranging from $75,000 to $140,000 depending on specialization.',
    salaryRange: '$65,000 - $140,000+',
  },
  106: {
    programId: 106,
    areaId: 1,
    name: 'Finance',
    headline: 'Top Accredited Online Finance Programs of 2026',
    description:
      'Master financial analysis, investment strategies, and corporate finance with an online finance degree. Prepare for careers in banking, investment management, and financial planning.',
    whyStudy:
      'Finance professionals command high salaries and work in dynamic, fast-paced environments analyzing markets, managing investments, and advising on financial strategy.',
    careerOutlook:
      'Financial analysts earn a median salary of $95,080, with 8% projected job growth.',
    salaryRange: '$70,000 - $150,000+',
  },
  109: {
    programId: 109,
    areaId: 1,
    name: 'Digital Marketing & Advertising',
    headline:
      'Top Accredited Online Digital Marketing & Advertising Programs of 2026',
    description:
      'Master digital marketing strategies, social media, SEO, and data analytics. Prepare for careers in marketing management, digital strategy, and brand development.',
    whyStudy:
      'Digital marketing is essential for modern business. This field combines creativity with data analysis and offers diverse career paths in an ever-evolving industry.',
    careerOutlook:
      'Marketing managers earn a median salary of $140,040, with 6% projected growth.',
    salaryRange: '$55,000 - $140,000+',
  },
  // Criminal Justice Programs
  203: {
    programId: 203,
    areaId: 2,
    name: 'Criminal Justice',
    headline: 'Top Accredited Online Criminal Justice Programs of 2026',
    description:
      'Prepare for a career in law enforcement, corrections, or legal services with a comprehensive criminal justice education covering criminology, law, and the justice system.',
    whyStudy:
      'Criminal justice professionals make a direct impact on public safety and community well-being while enjoying job stability and diverse career paths.',
    careerOutlook:
      'Steady demand for criminal justice professionals, with police officers earning a median of $65,790.',
    salaryRange: '$50,000 - $90,000+',
  },
  205: {
    programId: 205,
    areaId: 2,
    name: 'Cyber Security',
    headline: 'Top Accredited Online Cyber Security Programs of 2026',
    description:
      'Protect organizations from digital threats with a cybersecurity degree. Learn threat analysis, network security, and incident response in this high-demand field.',
    whyStudy:
      'Cybersecurity is one of the fastest-growing fields with critical workforce shortages, offering excellent job security and high salaries.',
    careerOutlook:
      'Cybersecurity analysts earn a median salary of $120,360, with 32% projected growth.',
    salaryRange: '$80,000 - $150,000+',
  },
  // Psychology Programs
  305: {
    programId: 305,
    areaId: 3,
    name: 'Counseling',
    headline: 'Top Accredited Online Counseling Programs of 2026',
    description:
      'Prepare for a rewarding career helping others with a counseling degree. Build skills in therapeutic techniques, assessment, and client care.',
    whyStudy:
      "Counseling is a deeply fulfilling career that allows you to make a meaningful difference in people's lives while enjoying strong job growth and stability.",
    careerOutlook:
      'Substance abuse and behavioral disorder counselors are projected to grow 18%, with median salaries of $53,710.',
    salaryRange: '$45,000 - $75,000+',
  },
  // IT Programs
  406: {
    programId: 406,
    areaId: 4,
    name: 'Information Technology',
    headline: 'Top Accredited Online Information Technology Programs of 2026',
    description:
      'Build technical expertise in systems administration, networking, and IT management. Prepare for careers supporting and managing technology infrastructure.',
    whyStudy:
      'IT professionals are essential to every modern organization, offering excellent job security, remote work opportunities, and strong earning potential.',
    careerOutlook:
      'IT managers earn a median salary of $164,070, with 15% projected growth.',
    salaryRange: '$70,000 - $165,000+',
  },
  410: {
    programId: 410,
    areaId: 4,
    name: 'Software Engineering',
    headline: 'Top Accredited Online Software Engineering Programs of 2026',
    description:
      'Learn to design, develop, and maintain software systems. Master programming, algorithms, and software architecture in this high-demand field.',
    whyStudy:
      'Software engineers are among the highest-paid professionals with exceptional job growth, remote work flexibility, and opportunities to work on cutting-edge technology.',
    careerOutlook:
      'Software developers earn a median salary of $132,270, with 25% projected growth.',
    salaryRange: '$90,000 - $180,000+',
  },
  // Healthcare Programs
  506: {
    programId: 506,
    areaId: 5,
    name: 'Healthcare Administration',
    headline:
      'Top Accredited Online Healthcare Administration Programs of 2026',
    description:
      'Lead healthcare organizations with a degree in healthcare administration. Manage operations, budgets, and staff in hospitals, clinics, and health systems.',
    whyStudy:
      'Healthcare administration offers the opportunity to improve patient care and health outcomes without direct clinical work, with exceptional job growth.',
    careerOutlook:
      'Medical and health services managers earn a median salary of $110,680, with 28% projected growth.',
    salaryRange: '$75,000 - $150,000+',
  },
  // Nursing Programs
  1003: {
    programId: 1003,
    areaId: 10,
    name: 'RN to BSN',
    headline: 'Top Accredited Online RN to BSN Programs of 2026',
    description:
      'Advance your nursing career with a BSN. Designed for working RNs, these programs offer flexible online coursework with local clinical requirements.',
    whyStudy:
      'A BSN is increasingly required by hospitals and health systems, opening doors to leadership roles, specialized positions, and higher salaries.',
    careerOutlook:
      'BSN-prepared nurses earn higher salaries and qualify for management and advanced practice roles. RNs earn a median of $86,070.',
    salaryRange: '$75,000 - $120,000+',
  },
}

// Helper: Get program content or fall back to area content
export function getProgramContent(programId: number): ProgramContent | null {
  return programContent[programId] || null
}

// Helper: Get all programs for an area (for dropdown)
export function getProgramsForArea(areaId: number) {
  return programOptions[areaId] || []
}
